// ─── Skills Admin Page — Phase 1 stub ───────────────────────────
// Read-only listing of skills from the skills library.
// AC-5: Table with search, category filter, status badges.

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Loader2, BookOpen } from 'lucide-react';
import api from '../api';
import { useBrandedDialog } from '../components/BrandedDialog';

interface Skill {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  description: string;
  stack_tags: string[];
  source?: string;
  version?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: 'var(--green-dim, rgba(34,197,94,0.1))', text: 'var(--green, #22c55e)' },
  deprecated: { bg: 'var(--red-dim, rgba(239,68,68,0.1))', text: 'var(--red, #ef4444)' },
  draft: { bg: 'rgba(234,179,8,0.1)', text: '#eab308' },
};

export function SkillsPage() {
  const { alert: brandedAlert } = useBrandedDialog();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const loadSkills = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (category) params.set('category', category);
      const data = await api.listSkills(params.toString());
      setSkills(data.skills ?? []);
    } catch (err) {
      console.error('Failed to load skills:', err);
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const categories = [...new Set(skills.map(s => s.category))].sort();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOpen size={18} style={{ color: 'var(--orange)' }} />
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Skills Library</h2>
          <span style={{ fontSize: 12, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
            {skills.length} skills
          </span>
          {skills.length > 0 && skills[0].updated_at && (
            <span style={{ fontSize: 10, color: 'var(--text-ghost)', fontFamily: 'var(--font-mono)', marginLeft: 8 }}>
              Last ingestion: {new Date(skills[0].updated_at).toLocaleString()}
            </span>
          )}
        </div>
        <button
          onClick={() => { void brandedAlert('Add skill — coming in Phase 2'); }}
          style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600,
            background: 'var(--orange)', border: 'none', borderRadius: 6,
            color: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <Plus size={14} /> Add Skill
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '6px 10px',
        }}>
          <Search size={14} style={{ color: 'var(--text-dim)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search skills..."
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-mono)',
            }}
          />
        </div>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: 12, background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: 6,
            color: 'var(--text)', fontFamily: 'var(--font-mono)', cursor: 'pointer',
          }}
        >
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-dim)' }} />
        </div>
      ) : skills.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
          <BookOpen size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
          <p style={{ fontSize: 14 }}>No skills found.</p>
          <p style={{ fontSize: 12 }}>Skills will be populated during Phase 2 — skill extraction from existing products.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table className="ocn-grid">
            <thead>
              <tr style={{ background: 'var(--surface2)' }}>
                <th className="nowrap" style={thStyle}>Name</th>
                <th className="nowrap" style={thStyle}>Category</th>
                <th className="nowrap" style={thStyle}>Stack</th>
                <th className="nowrap" style={thStyle}>Status</th>
                <th className="nowrap" style={thStyle}>Source</th>
                <th className="nowrap" style={thStyle}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {skills.map(skill => (
                <tr key={skill.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>{skill.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                      {skill.description.slice(0, 80)}{skill.description.length > 80 ? '...' : ''}
                    </div>
                  </td>
                  <td className="nowrap" style={tdStyle}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)' }}>
                      {skill.category}{skill.subcategory ? `/${skill.subcategory}` : ''}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      {(skill.stack_tags ?? []).slice(0, 3).map(tag => (
                        <span key={tag} style={{
                          padding: '1px 6px', fontSize: 10, borderRadius: 3,
                          background: 'var(--surface3)', color: 'var(--text-dim)',
                          fontFamily: 'var(--font-mono)',
                        }}>
                          {tag}
                        </span>
                      ))}
                      {(skill.stack_tags ?? []).length > 3 && (
                        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>+{skill.stack_tags.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', fontSize: 10, borderRadius: 4, fontWeight: 600,
                      background: STATUS_COLORS[skill.status]?.bg ?? 'var(--surface3)',
                      color: STATUS_COLORS[skill.status]?.text ?? 'var(--text-dim)',
                    }}>
                      {skill.status}
                    </span>
                  </td>
                  <td className="nowrap" style={tdStyle}>
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                      {skill.source ?? '—'}
                    </span>
                  </td>
                  <td className="nowrap" style={tdStyle}>
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                      {new Date(skill.updated_at).toLocaleDateString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontWeight: 600,
  color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px', verticalAlign: 'top',
};
